/**
 * Inline SVG of an engineer wearing a hard hat. Stylized, technical, no emoji.
 *
 * Two variants:
 *  - <Engineer />          full bust (used in the hero)
 *  - <EngineerHead />      head + helmet only (avatar inside speech bubbles)
 */

interface Props {
  size?: number;
  className?: string;
}

export function Engineer({ size = 220, className }: Props) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 240 280"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="Ingeniero estructural"
      role="img"
    >
      {/* Background blueprint disc */}
      <defs>
        <radialGradient id="eng-bg" cx="50%" cy="40%" r="65%">
          <stop offset="0%" stopColor="rgba(245,158,11,0.10)" />
          <stop offset="100%" stopColor="rgba(245,158,11,0)" />
        </radialGradient>
        <pattern id="eng-grid" width="14" height="14" patternUnits="userSpaceOnUse">
          <path d="M 14 0 L 0 0 0 14" fill="none" stroke="rgba(148,163,184,0.10)" strokeWidth="0.7" />
        </pattern>
      </defs>
      <circle cx="120" cy="120" r="120" fill="url(#eng-bg)" />
      <circle cx="120" cy="120" r="115" fill="url(#eng-grid)" />

      {/* Hi-vis vest collar (back layer) */}
      <path
        d="M 40 270 C 50 220 80 200 120 200 C 160 200 190 220 200 270 Z"
        fill="#F59E0B"
        stroke="#B45309"
        strokeWidth="2"
      />
      {/* Reflective stripes on vest */}
      <path d="M 60 250 L 180 250" stroke="#F8FAFC" strokeWidth="3" strokeDasharray="6 4" opacity="0.85" />
      <path d="M 60 262 L 180 262" stroke="#F8FAFC" strokeWidth="3" strokeDasharray="6 4" opacity="0.85" />

      {/* Shirt under vest */}
      <path d="M 88 200 L 120 218 L 152 200 L 152 230 L 88 230 Z" fill="#1E293B" />
      {/* Tie-ish detail */}
      <path d="M 116 218 L 124 218 L 122 240 L 118 240 Z" fill="#0F172A" />

      {/* Neck */}
      <path d="M 108 188 L 132 188 L 132 208 L 108 208 Z" fill="#E5C29B" />
      <path d="M 108 188 L 132 188 L 132 196 L 108 196 Z" fill="rgba(0,0,0,0.10)" />

      {/* Head - rounded square for a 'technical' feel */}
      <path
        d="M 88 110 Q 88 88 100 80 Q 110 74 120 74 Q 130 74 140 80 Q 152 88 152 110 L 152 158 Q 152 180 130 188 L 110 188 Q 88 180 88 158 Z"
        fill="#E5C29B"
        stroke="#A0703F"
        strokeWidth="1.5"
      />
      {/* Ear */}
      <ellipse cx="89" cy="138" rx="4" ry="7" fill="#D5A878" />
      <ellipse cx="151" cy="138" rx="4" ry="7" fill="#D5A878" />

      {/* Hair fringe under helmet */}
      <path d="M 92 110 Q 110 100 148 110 L 148 116 Q 110 108 92 116 Z" fill="#3B2A1A" />

      {/* Eyebrows */}
      <path d="M 100 128 Q 108 124 116 128" stroke="#3B2A1A" strokeWidth="2.5" fill="none" strokeLinecap="round" />
      <path d="M 124 128 Q 132 124 140 128" stroke="#3B2A1A" strokeWidth="2.5" fill="none" strokeLinecap="round" />

      {/* Eyes */}
      <circle cx="108" cy="138" r="2.6" fill="#0F172A" />
      <circle cx="132" cy="138" r="2.6" fill="#0F172A" />
      <circle cx="108.7" cy="137.3" r="0.9" fill="#F8FAFC" />
      <circle cx="132.7" cy="137.3" r="0.9" fill="#F8FAFC" />

      {/* Nose */}
      <path d="M 120 142 L 116 156 L 122 158" stroke="#A0703F" strokeWidth="1.5" fill="none" strokeLinecap="round" />

      {/* Mouth - confident half smile */}
      <path d="M 110 168 Q 120 174 132 168" stroke="#0F172A" strokeWidth="2" fill="none" strokeLinecap="round" />

      {/* Hard hat */}
      {/* Brim */}
      <path
        d="M 80 110 Q 120 96 160 110 L 160 116 Q 120 102 80 116 Z"
        fill="#F59E0B"
        stroke="#B45309"
        strokeWidth="1.5"
      />
      {/* Dome */}
      <path
        d="M 88 110 Q 88 70 120 64 Q 152 70 152 110 Z"
        fill="#F59E0B"
        stroke="#B45309"
        strokeWidth="1.8"
      />
      {/* Helmet ridges */}
      <path d="M 120 64 L 120 110" stroke="#B45309" strokeWidth="1.5" fill="none" />
      <path d="M 102 70 Q 102 92 100 110" stroke="#B45309" strokeWidth="1" fill="none" />
      <path d="M 138 70 Q 138 92 140 110" stroke="#B45309" strokeWidth="1" fill="none" />
      {/* Helmet front badge - alpha mark */}
      <rect x="111" y="84" width="18" height="14" rx="2" fill="#0F172A" />
      <path
        d="M 115 96 L 120 86 L 125 96 M 117 92 L 123 92"
        stroke="#F59E0B"
        strokeWidth="1.4"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function EngineerHead({ size = 48, className }: Props) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="Ingeniero"
      role="img"
    >
      <circle cx="50" cy="50" r="48" fill="rgba(245,158,11,0.12)" />
      <circle cx="50" cy="50" r="48" fill="none" stroke="rgba(245,158,11,0.35)" />
      {/* Head */}
      <path
        d="M 30 50 Q 30 36 38 32 Q 44 28 50 28 Q 56 28 62 32 Q 70 36 70 50 L 70 68 Q 70 78 60 82 L 40 82 Q 30 78 30 68 Z"
        fill="#E5C29B"
        stroke="#A0703F"
        strokeWidth="0.9"
      />
      {/* Eyes */}
      <circle cx="42" cy="58" r="1.6" fill="#0F172A" />
      <circle cx="58" cy="58" r="1.6" fill="#0F172A" />
      {/* Smile */}
      <path d="M 44 70 Q 50 73 56 70" stroke="#0F172A" strokeWidth="1.2" fill="none" strokeLinecap="round" />
      {/* Hard hat */}
      <path d="M 26 50 Q 50 42 74 50 L 74 53 Q 50 46 26 53 Z" fill="#F59E0B" stroke="#B45309" strokeWidth="0.8" />
      <path d="M 32 50 Q 32 26 50 22 Q 68 26 68 50 Z" fill="#F59E0B" stroke="#B45309" strokeWidth="0.9" />
      <rect x="46" y="32" width="8" height="6" rx="1" fill="#0F172A" />
      <path d="M 47.5 38 L 50 33 L 52.5 38 M 48.5 36 L 51.5 36" stroke="#F59E0B" strokeWidth="0.9" fill="none" />
    </svg>
  );
}
